/**
 * IncidentReportDetailView - Full read-only report view for closed incidents
 * Used in History tab to show complete incident details
 * Follows DefectDetailView pattern
 */

import React from 'react';
import {
  AlertTriangle,
  Calendar,
  MapPin,
  User,
  Clock,
  FileText,
  Search,
  CheckCircle,
  Shield,
  Users,
  XCircle
} from 'lucide-react';
// FT-357: shell-level back handler — bottom bar owns Back. Header BackButton removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';
import { useCrew } from '../../../../hooks/useCrew';
import { getCreatedByName } from '../../shared/AttributionBadge';
import { formatUserIdentity } from '../../../../utils/userDisplay';
// FT-189: Centralized crew name resolution
import { resolveCrewName } from '../crew/utils/crewHelpers';

const IncidentReportDetailView = ({ incident, onBack }) => {
  // FT-357: register onBack with the shell-level MobileBottomBar. Header
  // BackButton removed — bottom bar owns Back.
  useSetBackHandler(onBack);

  const { crew } = useCrew();

  // FT-189: Use centralized crew name resolution helper
  const getCrewDisplayName = (crewId) => {
    if (!crewId) return 'Not specified';
    return resolveCrewName(crewId, crew || []);
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateValue) => {
    if (!dateValue) return '-';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'critical': 'bg-red-100 text-red-700 border-red-200',
      'high': 'bg-orange-100 text-orange-700 border-orange-200',
      'medium': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'low': 'bg-blue-100 text-blue-700 border-blue-200'
    };
    return colors[severity?.toLowerCase()] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getTypeLabel = (type) => {
    const types = {
      'accident': 'Accident',
      'near_miss': 'Near Miss',
      'hazard': 'Hazard/Unsafe Condition',
      'environmental': 'Environmental',
      'equipment_failure': 'Equipment Failure',
      'personnel_injury': 'Personnel Injury',
      'safety': 'Safety'
    };
    return types[type] || type || 'Unknown';
  };

  if (!incident) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* FT-357: header BackButton removed — bottom bar owns Back. */}
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                    Incident Report
                  </h1>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    Closed
                  </span>
                </div>
                <p className="text-sm text-gray-500">
                  {incident.id} • {getTypeLabel(incident.type)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* Severity Banner */}
        <div className={`flex items-center gap-3 p-4 rounded-lg border ${getSeverityColor(incident.severity)}`}>
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <div>
            <span className="font-medium">
              {incident.severity?.charAt(0).toUpperCase() + incident.severity?.slice(1)} Severity Incident
            </span>
            <span className="mx-2">•</span>
            <span>{getTypeLabel(incident.type)}</span>
          </div>
        </div>

        {/* Section 1: Incident Details */}
        <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          <div className="bg-slate-700 px-5 py-3">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Incident Details
            </h2>
          </div>

          <div className="px-6 py-5 space-y-4">
            {/* Key Info Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Date</label>
                <div className="flex items-center gap-2 text-sm text-gray-900">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  {formatDate(incident.date)}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Time</label>
                <div className="flex items-center gap-2 text-sm text-gray-900">
                  <Clock className="h-4 w-4 text-gray-400" />
                  {incident.time || '-'}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Location</label>
                <div className="flex items-center gap-2 text-sm text-gray-900">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  {incident.location || '-'}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Reported By</label>
                <div className="flex items-center gap-2 text-sm text-gray-900">
                  <User className="h-4 w-4 text-gray-400" />
                  {getCrewDisplayName(incident.reportedBy)}
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Description</label>
              <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700">
                {incident.description || 'No description provided'}
              </div>
            </div>

            {/* Immediate Actions */}
            {incident.immediateActions && (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Immediate Actions Taken</label>
                <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-800">
                  {incident.immediateActions}
                </div>
              </div>
            )}

            {/* People Involved */}
            {(incident.involvedPersons?.length > 0 || incident.personsInvolved?.length > 0) && (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">People Involved</label>
                <div className="flex flex-wrap gap-2">
                  {(incident.involvedPersons || incident.personsInvolved || []).map((person, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                      <Users className="h-3 w-3" />
                      {getCrewDisplayName(person)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Injury/Damage Indicators */}
            {(incident.injuries || incident.damage || incident.pollutionRisk) && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {incident.injuries && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2 text-red-700 font-medium text-sm mb-1">
                      <XCircle className="h-4 w-4" />
                      Injuries Occurred
                    </div>
                    {incident.injuryDetails && (
                      <p className="text-sm text-red-600">{incident.injuryDetails}</p>
                    )}
                  </div>
                )}
                {incident.damage && (
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-center gap-2 text-orange-700 font-medium text-sm mb-1">
                      <AlertTriangle className="h-4 w-4" />
                      Damage Occurred
                    </div>
                    {incident.damageDetails && (
                      <p className="text-sm text-orange-600">{incident.damageDetails}</p>
                    )}
                  </div>
                )}
                {incident.pollutionRisk && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 text-amber-700 font-medium text-sm">
                      <AlertTriangle className="h-4 w-4" />
                      Pollution Risk
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Section 2: Investigation */}
        <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          <div className="bg-slate-700 px-5 py-3">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <Search className="h-5 w-5" />
              Investigation
            </h2>
          </div>

          <div className="px-6 py-5 space-y-4">
            {/* Investigation Meta */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Investigator</label>
                <div className="flex items-center gap-2 text-sm text-gray-900">
                  <User className="h-4 w-4 text-gray-400" />
                  {getCrewDisplayName(incident.investigator) || 'Not assigned'}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Status</label>
                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {incident.investigationStatus === 'completed' ? 'Completed' : incident.investigationStatus || 'Completed'}
                </span>
              </div>
            </div>

            {/* Root Cause */}
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Root Cause</label>
              <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700">
                {incident.rootCause || 'Not documented'}
              </div>
            </div>

            {/* Contributing Factors */}
            {incident.contributingFactors?.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Contributing Factors</label>
                <div className="flex flex-wrap gap-2">
                  {incident.contributingFactors.map((factor, idx) => (
                    <span key={idx} className="inline-flex items-center px-3 py-1 bg-amber-100 text-amber-700 text-sm rounded-full">
                      {factor}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Lessons Learned */}
            {incident.lessonsLearned && (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Lessons Learned</label>
                <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-800">
                  {incident.lessonsLearned}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section 3: Corrective Actions */}
        {incident.remedialActions?.length > 0 && (
          <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Corrective Actions
                <span className="ml-2 px-2 py-0.5 bg-white/20 text-white text-xs rounded">
                  {incident.remedialActions.filter(a => a.completed).length}/{incident.remedialActions.length} Complete
                </span>
              </h2>
            </div>

            <div className="px-6 py-5">
              <div className="space-y-3">
                {incident.remedialActions.map((action, idx) => (
                  <div
                    key={action.id || idx}
                    className={`flex items-start gap-3 p-4 rounded-lg border ${
                      action.completed ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'
                    }`}
                  >
                    <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                      action.completed ? 'bg-green-500 text-white' : 'bg-yellow-400 text-white'
                    }`}>
                      {action.completed ? (
                        <CheckCircle className="h-3 w-3" />
                      ) : (
                        <Clock className="h-3 w-3" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${action.completed ? 'text-green-800' : 'text-yellow-800'}`}>
                        {action.description}
                      </p>
                      <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
                        {action.assignedTo && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {getCrewDisplayName(action.assignedTo)}
                          </span>
                        )}
                        {action.dueDate && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Due: {formatDate(action.dueDate)}
                          </span>
                        )}
                        {action.completedAt && (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="h-3 w-3" />
                            Completed: {formatDate(action.completedAt)}
                          </span>
                        )}
                      </div>
                      {action.completionNotes && (
                        <p className="mt-2 text-xs text-gray-600 italic">
                          "{action.completionNotes}"
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Section 4: Preventive Measures */}
        {incident.preventiveMeasures && (
          <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Preventive Measures
              </h2>
            </div>

            <div className="px-6 py-5">
              <div className="p-4 bg-green-50 rounded-lg text-sm text-green-800">
                {incident.preventiveMeasures}
              </div>
            </div>
          </div>
        )}

        {/* Section 5: Closure Details */}
        <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          <div className="bg-slate-700 px-5 py-3">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Closure Details
            </h2>
          </div>

          <div className="px-6 py-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Closed Date</label>
                <div className="text-sm text-gray-900">{formatDateTime(incident.closedAt)}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Closed By</label>
                {/* FT-06e: Show "Name (Role)" format */}
                <div className="text-sm text-gray-900">{formatUserIdentity(incident.closedByName || incident.closedBy, incident.closedByRole) || '-'}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Close Type</label>
                <div className="text-sm text-gray-900">
                  {incident.quickClosed ? (
                    <span className="text-amber-600">Quick Close</span>
                  ) : (
                    <span className="text-green-600">Full Close</span>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Effectiveness</label>
                <div className="text-sm">
                  {incident.effectivenessVerified ? (
                    <span className="inline-flex items-center gap-1 text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      Verified
                    </span>
                  ) : (
                    <span className="text-gray-500">Not verified</span>
                  )}
                </div>
              </div>
            </div>

            {/* External Reporting */}
            {(incident.maibReportRequired || incident.caReportRequired || incident.escalatedToShore) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">External Reporting</label>
                <div className="flex flex-wrap gap-3">
                  {incident.maibReportRequired && (
                    <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm">
                      <div className="font-medium text-red-700">MAIB Reported</div>
                      {incident.maibReference && (
                        <div className="text-red-600 text-xs">Ref: {incident.maibReference}</div>
                      )}
                      {incident.maibReportedAt && (
                        <div className="text-red-600 text-xs">{formatDate(incident.maibReportedAt)}</div>
                      )}
                    </div>
                  )}
                  {incident.caReportRequired && (
                    <div className="px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg text-sm">
                      <div className="font-medium text-orange-700">CA Reported</div>
                      {incident.caReportedAt && (
                        <div className="text-orange-600 text-xs">{formatDate(incident.caReportedAt)}</div>
                      )}
                    </div>
                  )}
                  {incident.escalatedToShore && (
                    <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                      <div className="font-medium text-blue-700">Escalated to Shore</div>
                      {incident.escalatedAt && (
                        <div className="text-blue-600 text-xs">{formatDate(incident.escalatedAt)}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Audit Trail */}
        <div className="text-xs text-gray-400 text-center pt-4">
          Created: {formatDateTime(incident.createdAt)} by {getCreatedByName(incident.createdBy) || '-'}
          {incident.updatedAt && incident.updatedAt !== incident.createdAt && (
            <> • Last updated: {formatDateTime(incident.updatedAt)} by {getCreatedByName(incident.updatedBy) || '-'}</>
          )}
        </div>
      </div>
    </div>
  );
};

export default IncidentReportDetailView;
